import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Package, PushPin } from '@phosphor-icons/react';
import { getLocationContents, getLevelHeatmap } from '../../lib/api';
import { DaysPicker, SwapSuggestions, heatStyle } from './HeatBits';

const SkuLine = ({ s }) => (
  <div className="flex items-center justify-between gap-2 text-[11px]" data-testid={`sku-line-${s.sku_code}`}>
    <span className="font-mono font-bold truncate" title={s.name || ''}>{s.sku_code || '—'}</span>
    <span className="flex items-center gap-1 shrink-0">
      {s.fixed && (
        <Badge variant="outline" className="h-4 px-1 text-[9px] border-brand text-brand" title={`Fixed slot · min ${s.min_qty} / max ${s.max_qty}`}>
          <PushPin size={8} className="mr-0.5" />FIXED
        </Badge>
      )}
      <span className="font-mono">{Math.round(s.quantity)}</span>
    </span>
  </div>
);

const Cell = ({ loc, heat }) => {
  const picks = heat?.picks?.[loc?.id] || 0;
  return (
    <div
      className={`border p-2 min-h-[64px] ${loc?.skus?.length ? 'bg-card border-border' : 'bg-muted/40 border-dashed border-border'}`}
      style={heat ? heatStyle(picks, heat.max) : undefined}
      data-testid={`rack-cell-${loc?.location_code || 'empty'}`}
    >
      <p className="font-mono text-[10px] font-bold text-muted-foreground mb-1 flex justify-between gap-1">
        <span>{loc?.location_code || '—'}</span>
        {heat && loc && <span data-testid={`rack-cell-picks-${loc.location_code}`}>{picks} picks</span>}
      </p>
      {loc?.skus?.length ? loc.skus.map((s) => <SkuLine key={s.sku_id} s={s} />) : <p className="text-[10px] text-muted-foreground italic">Empty</p>}
    </div>
  );
};

const RackGrid = ({ tile, locations, heat }) => {
  const levels = tile.levels || 1;
  const depth = tile.depth || 1;
  const byPos = {};
  const others = [];
  locations.forEach((l) => {
    const lv = String(l.level || '').trim().toUpperCase();
    const li = /^[A-Z]$/.test(lv) ? lv.charCodeAt(0) - 65 : (/^\d+$/.test(lv) ? parseInt(lv, 10) - 1 : -1);
    const d = l.depth_position || 1;
    if (li >= 0 && li < levels && d <= depth && !byPos[`${li}-${d}`]) byPos[`${li}-${d}`] = l; else others.push(l);
  });
  const rows = Array.from({ length: levels }, (_, i) => levels - 1 - i); // top level first
  return (
    <div className="space-y-3">
      <div className="grid gap-1" style={{ gridTemplateColumns: `56px repeat(${depth}, minmax(0, 1fr))` }} data-testid="rack-grid">
        <div />
        {Array.from({ length: depth }, (_, d) => (
          <p key={d} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-center">{d === 0 ? 'Front' : `Deep ${d + 1}`}</p>
        ))}
        {rows.map((li) => (
          <React.Fragment key={li}>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold self-center" data-testid={`rack-level-label-${String.fromCharCode(65 + li)}`}>
              Level {String.fromCharCode(65 + li)}
              {heat && <span className="block normal-case tracking-normal font-mono">{heat.levelPicks?.[li] || 0} picks</span>}
            </p>
            {Array.from({ length: depth }, (_, d) => <Cell key={d} loc={byPos[`${li}-${d + 1}`]} heat={heat} />)}
          </React.Fragment>
        ))}
      </div>
      {others.length > 0 && (
        <div>
          <p className="overline mb-1">Other linked locations</p>
          <div className="grid grid-cols-3 gap-1">{others.map((l) => <Cell key={l.id} loc={l} heat={heat} />)}</div>
        </div>
      )}
    </div>
  );
};

const AllList = ({ locations }) => {
  const [q, setQ] = useState('');
  const [occupiedOnly, setOccupiedOnly] = useState(true);
  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return locations.filter((l) => (!occupiedOnly || l.skus.length) && (!n
      || (l.location_code || '').toLowerCase().includes(n)
      || l.skus.some((s) => (s.sku_code || '').toLowerCase().includes(n) || (s.name || '').toLowerCase().includes(n))));
  }, [locations, q, occupiedOnly]);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input placeholder="Search location or SKU…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="all-skus-search" />
        <button
          type="button" onClick={() => setOccupiedOnly((v) => !v)} data-testid="all-skus-occupied-toggle"
          className={`px-3 py-2 border text-xs font-bold uppercase whitespace-nowrap ${occupiedOnly ? 'bg-brand/10 border-brand text-brand' : 'border-border text-muted-foreground'}`}
        >
          {occupiedOnly ? 'Show all locations' : 'Show only with SKUs'}
        </button>
      </div>
      <div className="border border-border max-h-[28rem] overflow-y-auto" data-testid="all-skus-list">
        {rows.length === 0 ? <p className="p-4 text-xs text-muted-foreground">No locations match.</p> : rows.map((l) => (
          <div key={l.id} className="grid grid-cols-[160px_1fr] gap-3 px-3 py-2 border-b border-border last:border-b-0" data-testid={`all-skus-row-${l.location_code}`}>
            <div>
              <p className="font-mono font-bold text-xs">{l.location_code}</p>
              <p className="text-[10px] text-muted-foreground">{l.tile_label ? `Rack: ${l.tile_label}` : 'Not on layout'}</p>
            </div>
            <div className="space-y-0.5">
              {l.skus.length ? l.skus.map((s) => <SkuLine key={s.sku_id} s={s} />) : <p className="text-[10px] italic text-muted-foreground">Empty</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const LocationContentsDialog = ({ warehouseId, tile, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [days, setDays] = useState(90);
  const [heatData, setHeatData] = useState(null);
  useEffect(() => {
    getLocationContents(warehouseId, tile ? tile.location_ids || [] : null)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load SKUs'));
  }, [warehouseId, tile]);
  useEffect(() => {
    if (!tile) return;
    getLevelHeatmap(warehouseId, days).then((r) => setHeatData(r.data)).catch(() => setHeatData(null));
  }, [warehouseId, tile, days]);

  const heat = useMemo(() => {
    const t = heatData?.tiles?.find((x) => x.tile_id === tile?.id);
    if (!t) return null;
    const picks = {};
    const levelPicks = {};
    t.levels.forEach((lv) => {
      levelPicks[lv.level_index] = lv.picks;
      lv.positions.forEach((p) => { picks[p.location_id] = p.picks; });
    });
    return { picks, levelPicks, max: Math.max(0, ...Object.values(picks)) };
  }, [heatData, tile]);
  const rackSuggestions = (heatData?.suggestions || []).filter((s) => s.from.tile_id === tile?.id);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="location-contents-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span className="flex items-center gap-2">
              <Package size={16} weight="fill" className="text-brand" />
              {tile ? `SKUs in “${tile.label || tile.base_code || 'rack'}”` : 'All SKUs in this warehouse'}
            </span>
            {tile && <DaysPicker days={days} onChange={setDays} testid="rack-heat-days" />}
          </DialogTitle>
          {data && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="location-contents-summary">
              {data.sku_count} SKU{data.sku_count === 1 ? '' : 's'} across {data.occupied_locations} of {data.locations.length} locations · stock on hand plus fixed-location slots
            </p>
          )}
        </DialogHeader>
        {error && <p className="text-xs text-red-700" data-testid="location-contents-error">{error}</p>}
        {!data && !error && <p className="text-xs text-muted-foreground">Loading…</p>}
        {data && (tile ? (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <RackGrid tile={tile} locations={data.locations} heat={heat} />
            {heatData && <SwapSuggestions items={rackSuggestions} days={days} />}
          </div>
        ) : <AllList locations={data.locations} />)}
      </DialogContent>
    </Dialog>
  );
};
